'use client';

import React, { createContext, useContext } from 'react';

import { useLocalTheme } from './use-local-theme';
import { useThemeSnapshot } from './use-theme-snapshot';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
	theme: Theme;
	resolvedTheme: ResolvedTheme;
	toggleTheme: () => void;
	setTheme: (theme: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextType>({
	theme: 'dark',
	resolvedTheme: 'dark',
	toggleTheme: () => {},
	setTheme: () => {},
});

/** Lightweight ThemeProvider — reads from localStorage only, no auth/settings sync. */
export const ThemeProviderMinimal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const theme = useLocalTheme();
	return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};

/**
 * The theme for any consumer. `ready` is false during the server render and
 * the hydration pass, when `theme` / `resolvedTheme` are the shared fallback
 * rather than the visitor's preference: JS-driven work (canvas paint, class
 * toggles) should wait for it, markup should not depend on it.
 */
export const useThemeMinimal = () => {
	const context = useContext(ThemeContext);
	const { theme, resolvedTheme, ready } = useThemeSnapshot();
	return { ...context, theme, resolvedTheme, ready };
};
